import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { getMyPermissions } from "@/lib/permissionsApi";

// Cache the current user's effective permission map at AuthContext level
// would be ideal, but to keep AuthContext lean we hold a tiny module-level
// cache keyed by family id. Refreshed on demand via `refresh()`.
const _cache = new Map(); // family_id -> {effective, role, person_id}
const _listeners = new Set();

function _emit() { _listeners.forEach((fn) => fn()); }

export function clearPermissionsCache(familyId) {
  if (familyId) _cache.delete(familyId);
  else _cache.clear();
  _emit();
}

/**
 * `usePermission()` — without args returns a function `(key) -> boolean`.
 * `usePermission("event.create")` returns the boolean directly.
 *
 * Hides UI affordances the user can't use. Backend gates are the source of
 * truth — this is purely a UX hint. Defaults to `true` while loading so we
 * don't flash hidden buttons; the 403 will still surface a toast if the
 * client cache was stale.
 */
export function usePermission(key) {
  const { family } = useAuth();
  const [, force] = useState(0);
  const familyId = family?.id;

  useEffect(() => {
    if (!familyId) return undefined;
    const listener = () => force((n) => n + 1);
    _listeners.add(listener);
    if (!_cache.has(familyId)) {
      getMyPermissions(familyId).then((data) => {
        if (data) _cache.set(familyId, data);
        _emit();
      }).catch(() => {});
    }
    return () => { _listeners.delete(listener); };
  }, [familyId]);

  const data = familyId ? _cache.get(familyId) : null;
  const has = useCallback((k) => {
    if (!data) return true; // optimistic while loading
    return !!data.effective?.[k];
  }, [data]);

  if (key) return has(key);
  return has;
}

export function useMyRole() {
  const { family } = useAuth();
  return _cache.get(family?.id)?.role || null;
}
